import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { CornerMarks, Sparkline, StatusLED } from "@/components/infra";
import { LogoMark } from "@/components/hero/HeroShared";

export const fmt = (value) => (typeof value === "number" ? value.toLocaleString("en-US") : "—");
export const timeLabel = (iso) => {
  const date = iso ? new Date(typeof iso === "number" ? iso * 1000 : iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null;
};

export const statsFrom = (jobs) => [
  { id: "roles", value: fmt(jobs?.feed_total), label: "Open industry roles", note: "Live job feed" },
  { id: "employers", value: fmt(jobs?.employer_count), label: "Employer feeds", note: "Live careers sources" },
  { id: "early", value: fmt(jobs?.early_career_total), label: "Early-career roles", note: "Live job feed" },
  { id: "reach", value: "119.8M", label: "Media reach / month", note: "GlobeNewswire 2026" },
];

const Stock = ({ stock }) => {
  const up = stock.change_pct >= 0;
  const Arrow = up ? ArrowUpRight : ArrowDownRight;
  return (
    <li data-testid={`hero-option-b-stock-${stock.symbol.toLowerCase()}`} className="min-w-0 px-3 py-3 flex flex-col gap-1">
      <span className="flex items-center justify-between gap-2"><span className="font-mono-label text-[11px] !tracking-normal text-[#0F1117]">{stock.symbol}</span><span className={`inline-flex items-center text-xs font-semibold ${up ? "text-[#078552]" : "text-[#B4232C]"}`}><Arrow size={12} aria-hidden="true" />{up ? "+" : "−"}{Math.abs(stock.change_pct).toFixed(2)}%</span></span>
      <span className="text-[10px] text-[#71747B] truncate">{stock.name}</span>
      {Array.isArray(stock.spark) && stock.spark.length > 1 && <Sparkline data={stock.spark} trend={up ? "up" : "down"} className="w-full h-4" />}
    </li>
  );
};

export const LiveIndex = ({ live, className = "" }) => {
  const updated = timeLabel(live.updatedAt);
  return (
    <section data-testid="hero-option-b-live-stats" aria-label="DCYB live industry index" className={`chrome-surface relative min-w-0 fade-up delay-200 ${className}`}>
      <CornerMarks color="#A9ADB4" />
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b hairline">
        <span className="inline-flex items-center gap-2 font-mono-label text-[11px] !tracking-normal text-[#0F1117]"><StatusLED color="#10B981" />Live Industry Index</span>
        <span data-testid="hero-option-b-updated" className="font-mono-label text-[10px] !tracking-normal text-[#5F6168]">{live.loading ? "Syncing…" : updated ? `Updated ${updated}` : "Snapshot"}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_200px]">
        <div className="relative flex items-center justify-center px-6 py-8 sm:py-10 border-b sm:border-b-0 sm:border-r hairline overflow-hidden">
          <div aria-hidden="true" className="absolute inset-0 dot-lattice" style={{ maskImage: "radial-gradient(closest-side, #000 30%, transparent 100%)", WebkitMaskImage: "radial-gradient(closest-side, #000 30%, transparent 100%)" }} />
          <span aria-hidden="true" className="hero-b-glow" />
          <LogoMark testId="hero-option-b-mark" className="relative w-[150px] sm:w-[170px] xl:w-[200px] dcyb-mark-shadow dcyb-mark-float" />
        </div>
        <ul aria-label="Live industry stats" className="grid grid-cols-2 sm:grid-cols-1 divide-[#E3E5E9] sm:divide-y">
          {statsFrom(live.jobs).map(({ id, value, label, note }) => (
            <li key={id} data-testid={`hero-option-b-stat-${id}`} className="px-4 py-3.5 border-b sm:border-b-0 border-[#E3E5E9] odd:border-r sm:odd:border-r-0">
              <span className="block font-display text-xl xl:text-2xl font-bold text-[#0F1117] whitespace-nowrap">{value}</span>
              <span className="block mt-1 font-mono-label text-[10px] !tracking-normal text-[#3D4049]">{label}</span>
              <span className="block text-[10px] text-[#71747B]">{note}</span>
            </li>
          ))}
        </ul>
      </div>
      {live.stocks.length > 0 && (
        <ul data-testid="hero-option-b-stocks" aria-label="Digital infrastructure stocks today" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 border-t hairline divide-x divide-[#E3E5E9]">
          {live.stocks.map((stock) => <Stock key={stock.symbol} stock={stock} />)}
        </ul>
      )}
    </section>
  );
};
